import type {IssueField} from '@github-ui/item-picker/IssueFieldPicker'
import {graphql} from 'relay-runtime'
import type {FieldsSectionFragment$key} from './__generated__/FieldsSectionFragment.graphql'
import {useFragment, useRelayEnvironment} from 'react-relay'
import {useCallback, useState, useEffect, Fragment} from 'react'
import type {FieldsSectionFieldValues$key} from './__generated__/FieldsSectionFieldValues.graphql'
import {IssueFieldSingleSelectEditor, IssueFieldTextEditor} from './FieldEditors'
import type {IssueFieldSingleSelectOption} from '@github-ui/item-picker/IssueSingleSelectFieldPicker'
import {
  isIssueFieldWithUnsavedValue,
  isSingleSelectField,
  isSingleSelectFieldWithValue,
  isTextField,
  type IssueFieldWithUnsavedValue,
} from '../../utils/issue-field-helper'
import {FieldsSelector} from './FieldSelector'
import {commitSetIssueFieldValueMutation} from './mutations/set-issue-field-value-mutation'
import type RelayModernEnvironment from 'relay-runtime/lib/store/RelayModernEnvironment'
import {ConfirmationDialog} from '@primer/react'
import {LABELS} from './constants/labels'

function ErrorDialog({onClose}: {onClose: () => void}) {
  return (
    <ConfirmationDialog title="Error" confirmButtonContent="OK" confirmButtonType="primary" onClose={onClose}>
      {LABELS.errorMessage}
    </ConfirmationDialog>
  )
}

export type CreateIssueFieldsSectionProps = {
  owner: string
  onSelectionChange: (fields: IssueFieldWithUnsavedValue[]) => void
  shortcutEnabled: boolean
}

// a shared helper for setting issue field value via mutation (new input shape)
function setIssueFieldValueHelper({
  environment,
  issueId,
  fieldId,
  textValue,
  singleSelectOptionId,
  deleteValue,
  onError,
}: {
  environment: RelayModernEnvironment
  issueId: string
  fieldId: string
  textValue?: string
  singleSelectOptionId?: string
  deleteValue?: boolean
  onError?: () => void
}) {
  commitSetIssueFieldValueMutation({
    environment,
    input: {
      issueId,
      issueFields: [
        {
          fieldId,
          ...(textValue !== undefined && {textValue}),
          ...(singleSelectOptionId !== undefined && {singleSelectOptionId}),
          ...(deleteValue && {delete: true}),
        },
      ],
    },
    onCompleted: () => {},
    onError: error => {
      // eslint-disable-next-line no-console
      console.error('Error setting issue field value:', error)
      onError?.()
    },
  })
}

export function CreateIssueFieldsSection({owner, onSelectionChange, shortcutEnabled}: CreateIssueFieldsSectionProps) {
  const [fields, setFields] = useState<IssueFieldWithUnsavedValue[]>([])
  const [fieldsSet, setFieldsSet] = useState<string[]>([])

  useEffect(() => {
    onSelectionChange(fields)
  }, [fields, onSelectionChange])

  const onIssueFieldSelected = useCallback(
    (selectedField: IssueField, selectedOption?: IssueFieldSingleSelectOption | null) => {
      if (isTextField(selectedField)) {
        setFields(prevFields => {
          const newFields = [...prevFields]
          if (selectedField.name && !newFields.some(f => f.field.name === selectedField.name)) {
            newFields.push({field: selectedField, value: ''})
          }
          return newFields
        })
      } else if (isSingleSelectField(selectedField) && selectedOption !== undefined) {
        setFields(prevFields => {
          const newFields = [...prevFields]
          if (selectedField.name && !newFields.some(f => f.field.name === selectedField.name)) {
            newFields.push({
              field: selectedField,
              value: selectedOption,
            })
          }
          return newFields
        })
      }
    },
    [],
  )

  const onCommitTextField = useCallback((fieldId: string, value: string) => {
    setFields(prevFields => {
      return prevFields.map(field => {
        if (isIssueFieldWithUnsavedValue(field) && field.field.id === fieldId) {
          return {...field, value}
        }
        return field
      })
    })
  }, [])

  const onCommitSingleSelectField = useCallback((fieldId: string, value: IssueFieldSingleSelectOption | null) => {
    setFields(prevFields => {
      return prevFields.map(field => {
        if (isSingleSelectFieldWithValue(field) && field.field.id === fieldId) {
          return {...field, value}
        }
        return field
      })
    })
  }, [])

  return (
    <>
      {fields.map((field, index) => {
        if (!field.field.id) return null
        const isLastItem = index === fields.length - 1
        if (isIssueFieldWithUnsavedValue(field)) {
          return (
            <IssueFieldTextEditor
              key={field.field.name}
              fieldId={field.field.id}
              fieldName={field.field.name || ''}
              readonly={false}
              initialValue={field.value || ''}
              onCommit={onCommitTextField}
              hideDivider={isLastItem}
            />
          )
        } else if (isSingleSelectFieldWithValue(field)) {
          return (
            <IssueFieldSingleSelectEditor
              key={field.field.name}
              fieldId={field.field.id}
              fieldName={field.field.name || ''}
              readonly={false}
              initialValue={
                field.value
                  ? {
                      name: field.value.name || '',
                      color: field.value.color || '',
                      description: field.value.description || '',
                    }
                  : null
              }
              onCommit={onCommitSingleSelectField}
              hideDivider={isLastItem}
            />
          )
        }
      })}
      <FieldsSelector
        owner={owner}
        fieldsSet={fieldsSet}
        setFieldsSet={setFieldsSet}
        onIssueFieldSelected={onIssueFieldSelected}
        shortcutEnabled={shortcutEnabled}
      />
    </>
  )
}

export type EditIssueFieldsSectionProps = {
  onIssueUpdate?: () => void
  singleKeyShortcutsEnabled: boolean
  issue: FieldsSectionFragment$key
  insideSidePanel?: boolean
}

export function EditIssueFieldsSection({issue, singleKeyShortcutsEnabled}: EditIssueFieldsSectionProps) {
  const [fieldsSet, setFieldsSet] = useState<string[]>([])
  const [errorPresent, setErrorPresent] = useState<boolean>(false)
  const environment = useRelayEnvironment()

  const data = useFragment(
    graphql`
      fragment FieldsSectionFragment on Issue {
        id
        repository {
          owner {
            login
          }
        }
        viewerCanSetFields
        ...FieldsSectionFieldValues
      }
    `,
    issue,
  )
  const {
    repository: {
      owner: {login: owner},
    },
    viewerCanSetFields,
  } = data

  const handleErrors = useCallback(() => {
    setErrorPresent(true)
  }, [])

  const onIssueFieldSelected = useCallback(
    (selectedField: IssueField, selectedOption?: IssueFieldSingleSelectOption | null) => {
      if (!selectedField.id) return
      if (isTextField(selectedField)) {
        setIssueFieldValueHelper({
          environment,
          issueId: data.id,
          fieldId: selectedField.id,
          textValue: '',
          onError: handleErrors,
        })
      } else if (isSingleSelectField(selectedField) && selectedOption) {
        setIssueFieldValueHelper({
          environment,
          issueId: data.id,
          fieldId: selectedField.id,
          singleSelectOptionId: selectedOption.id,
          onError: handleErrors,
        })
      }
    },
    [environment, data.id, handleErrors],
  )

  return (
    <>
      <IssueFields issue={data} fieldsSet={fieldsSet} setFieldsSet={setFieldsSet} />
      {viewerCanSetFields && (
        <FieldsSelector
          owner={owner}
          fieldsSet={fieldsSet}
          setFieldsSet={setFieldsSet}
          onIssueFieldSelected={onIssueFieldSelected}
          shortcutEnabled={singleKeyShortcutsEnabled}
        />
      )}
      {errorPresent && <ErrorDialog onClose={() => setErrorPresent(false)} />}
    </>
  )
}

function IssueFields({
  issue,
  fieldsSet,
  setFieldsSet,
}: {
  issue: FieldsSectionFieldValues$key
  fieldsSet: string[]
  setFieldsSet: React.Dispatch<React.SetStateAction<string[]>>
}) {
  const environment = useRelayEnvironment()
  const [errorPresent, setErrorPresent] = useState<boolean>(false)
  const data = useFragment(
    graphql`
      fragment FieldsSectionFieldValues on Issue {
        id
        issueFieldValues(first: 25) {
          nodes {
            ... on IssueFieldTextValue {
              field {
                ... on IssueFieldText {
                  id
                  name
                  dataType
                }
              }
              value
            }
            ... on IssueFieldSingleSelectValue {
              field {
                ... on IssueFieldSingleSelect {
                  id
                  name
                  dataType
                }
              }
              name
              color
              description
            }
          }
        }
        viewerCanSetFields
      }
    `,
    issue,
  )

  const filteredFieldValues = (data.issueFieldValues?.nodes || []).filter(fv => !!fv && fv.field)

  const filteredFieldsNames = filteredFieldValues.map(fv => fv?.field?.name || '')

  const handleErrors = useCallback(() => {
    setErrorPresent(true)
  }, [])
  useEffect(() => {
    if (fieldsSet.join(',') !== filteredFieldsNames.join(',') && filteredFieldsNames.length > 0) {
      setFieldsSet(filteredFieldsNames)
    }
  }, [fieldsSet, filteredFieldsNames, setFieldsSet])

  const onCommitTextField = useCallback(
    (fieldId: string, value: string) => {
      if (value === '') {
        setIssueFieldValueHelper({
          environment,
          issueId: data.id,
          fieldId,
          deleteValue: true,
          onError: handleErrors,
        })
      } else {
        setIssueFieldValueHelper({
          environment,
          issueId: data.id,
          fieldId,
          textValue: value,
          onError: handleErrors,
        })
      }
    },
    [environment, data.id, handleErrors],
  )

  const onCommitSingleSelectField = useCallback(
    (fieldId: string, value: IssueFieldSingleSelectOption | null) => {
      if (!value || !value.id) {
        setIssueFieldValueHelper({
          environment,
          issueId: data.id,
          fieldId,
          singleSelectOptionId: '',
          deleteValue: true,
          onError: handleErrors,
        })
      } else {
        setIssueFieldValueHelper({
          environment,
          issueId: data.id,
          fieldId,
          singleSelectOptionId: String(value.id),
          onError: handleErrors,
        })
      }
    },
    [environment, data.id, handleErrors],
  )

  const fieldValues = filteredFieldValues.map((fieldValue, index) => {
    if (!fieldValue || !fieldValue.field || !fieldValue.field.id) return null
    const isLastItem = index === filteredFieldValues.length - 1

    if (fieldValue.field && fieldValue.field.dataType === 'TEXT') {
      return (
        <Fragment key={fieldValue.field.name}>
          <IssueFieldTextEditor
            key={fieldValue.field.name}
            fieldId={fieldValue.field.id}
            fieldName={fieldValue.field.name || ''}
            initialValue={fieldValue.value || ''}
            readonly={!data.viewerCanSetFields}
            onCommit={onCommitTextField}
            hideDivider={isLastItem}
          />
          {errorPresent && <ErrorDialog onClose={() => setErrorPresent(false)} />}
        </Fragment>
      )
    } else if (fieldValue.field.dataType === 'SINGLE_SELECT') {
      return (
        <Fragment key={fieldValue.field.name}>
          <IssueFieldSingleSelectEditor
            key={fieldValue.field.name}
            fieldId={fieldValue.field.id}
            fieldName={fieldValue.field.name || ''}
            initialValue={{
              name: fieldValue.name || '',
              color: fieldValue.color || '',
              description: fieldValue.description || '',
            }}
            readonly={!data.viewerCanSetFields}
            onCommit={onCommitSingleSelectField}
            hideDivider={isLastItem}
          />
          {errorPresent && <ErrorDialog onClose={() => setErrorPresent(false)} />}
        </Fragment>
      )
    } else {
      return null
    }
  })

  return <>{fieldValues}</>
}

try{ ErrorDialog.displayName ||= 'ErrorDialog' } catch {}
try{ CreateIssueFieldsSection.displayName ||= 'CreateIssueFieldsSection' } catch {}
try{ EditIssueFieldsSection.displayName ||= 'EditIssueFieldsSection' } catch {}
try{ IssueFields.displayName ||= 'IssueFields' } catch {}